import type { ID } from '../../../types/basic'

export interface DragDropArgs<T> {
  dropTarget: Element
  dropTargetIdPart: string
  dropSurface: 'before' | 'after'
  draggedData: T
}
export interface DragIdicatorStyleProps {
  intersectionRect: {
    height: number
    width: number
    left: number
    right: number
    top: number
    bottom: number
  }
  dropTarget: Element
  direction: 'vertical' | 'horizontal'
  dropSurface: string | 'before' | 'after'
}
export interface GwDragDropper {
  dropTargetIdPart: string
  direction?: 'vertical' | 'horizontal'
  getDragIdicatorStyle?: (args: DragIdicatorStyleProps) => any
}

export const getDragExpectedProps = <T extends { id: ID }>({
  items,
  draggedItems,
  dragArgs,
  sortOrder = 'ASC',
  orderingKey = 'ordering_position',
  getId = item => item.id.toString(),
}: {
  items: T[]
  draggedItems: T[]
  dragArgs: DragDropArgs<T>
  sortOrder?: 'ASC' | 'DESC'
  orderingKey?: 'ordering_position' | undefined
  getId?: (item: T) => ID
}) => {
  const { dropTarget, dropSurface, dropTargetIdPart } = dragArgs

  const dropItemId = dropTarget.id.replace(`${dropTargetIdPart}`, '')
  const dropItem = items[items.findIndex(i => getId(i) === dropItemId)]

  if (draggedItems.some(i => getId(i) === getId(dropItem))) {
    return {
      did_drop_on_dragged: true,
    }
  }

  const firstDraggedItem = draggedItems[0]
  const dropItemIdx = items.findIndex(i => getId(i) === getId(dropItem))
  const draggedItemIdx = items.findIndex(i => getId(i) === getId(firstDraggedItem))

  const afterVal = dropItemIdx < draggedItemIdx ? 1 : 0
  const beforeVal = dropItemIdx < draggedItemIdx ? 0 : -1

  const total = items.length
  const increment = dropSurface === 'after' ? afterVal : beforeVal
  let orderPos = dropItemIdx + increment
  orderPos = orderPos < 0 ? 0 : orderPos > total ? total : orderPos

  let expected_items = items.filter(i => !draggedItems.find(ii => getId(ii) === getId(i)))
  // @ts-expect-error
  expected_items.splice(orderPos, 0, draggedItems)
  // @ts-expect-error
  expected_items = expected_items.flat()

  if (sortOrder === 'ASC') {
    orderPos += 1
    expected_items =
      (orderingKey &&
        expected_items.map((i, idx) => ({
          ...i,
          [orderingKey]: idx + 1,
        }))) ||
      expected_items
  } else {
    orderPos = total - orderPos
    expected_items =
      (orderingKey &&
        expected_items.map((i, idx) => ({
          ...i,
          [orderingKey]: total - idx,
        }))) ||
      expected_items
  }

  return {
    did_drop_on_dragged: false, //
    expected_items,
    expected_ordering_position: orderPos,
  }
}
